import React, { useEffect, useMemo, useRef, useState } from "react";
import { userErrorMessage } from "../utils/userErrorMessage";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createApprovalFromDraft,
  getBugReportItems,
  getDriverChangeItems,
  getDriverCreateItems,
  getExpenseItems,
  getUserCreateItems,
  getVehicleChangeItems,
  interpretAiOpsMessage,
  makeBugReportDraft,
  makeDriverChangeDraft,
  makeDriverCreateDraft,
  makeDriverUpdateDraft,
  makeExpenseDraft,
  makeReservationDraft,
  makeSafetyDraft,
  makeUserCreateDraft,
  makeVehicleCreateDraft,
  makeVehicleUpdateDraft,
} from "../services/aiOps/aiOpsService";

const typeToRouteKey = {
  일반: "general",
  학단: "school",
  통근: "commute",
  투어: "tour",
};

function draftToPrefill(draft) {
  const summary = draft?.payload?.summary || {};
  const firstRow = draft?.payload?.reservations?.[0] || {};
  return {
    date: summary.start_date || firstRow.date || "",
    time: summary.time || firstRow.time || "",
    start: summary.start || firstRow.start || "",
    transit: summary.transit || firstRow.transit || "",
    destination: summary.destination || firstRow.destination || "",
    user: summary.user || firstRow.user || "",
    user_phone: summary.user_phone || firstRow.user_phone || "",
    money: summary.money ?? firstRow.money ?? "",
    note: firstRow.memo || "",
    seat: summary.car_type || firstRow.seat || "대형",
    seat_detail: summary.seat_detail || firstRow.seat_detail || "",
    round: firstRow.one_round || "왕복",
    buses: summary.number_of_car || 1,
    nights: summary.days || 0,
    car_number: firstRow.car_number || "",
  };
}

function buildReservationSearchPath(command = {}) {
  const params = new URLSearchParams();
  if (command.date) {
    params.set("from", command.date);
    params.set("to", command.date);
  }
  if (command.reservation_type) params.set("type", command.reservation_type);
  if (command.user) params.set("user", command.user);
  if (command.start) params.set("start", command.start);
  if (command.destination) params.set("dest", command.destination);

  const query = params.toString();
  return query ? `/reservation-search?${query}` : "/reservation-search";
}

function DraftSummary({ draft }) {
  if (!draft) return null;

  if (draft.type === "reservation_create") {
    const summary = draft.payload.summary;
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          <span>운행일</span><span>{summary.start_date} ~ {summary.end_date}</span>
          <span>유형</span><span>{summary.reservation_type}</span>
          <span>시간</span><span>{summary.time || "-"}</span>
          <span>경로</span><span>{summary.start || "-"} → {summary.destination || "-"}</span>
          <span>사용자</span><span>{summary.user || "-"}</span>
          <span>생성 행</span><span>{draft.payload.reservations.length}건</span>
        </div>
      </div>
    );
  }

  const updates = draft.afterData?.reservation_updates || [];
  const selected = draft.afterData?.selected_candidate || {};

  if (draft.type === "safety_create") {
    const safety = draft.payload?.safety || {};
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          <span>형태</span><span>{safety.issue || "-"}</span>
          <span>운전자</span><span>{safety.driver_name || "-"}</span>
          <span>자기 차량</span><span>{safety.car_number || "-"}</span>
          <span>상대 차량</span><span>{safety.opponent || "-"}</span>
          <span>일시</span><span>{safety.date || "-"}</span>
          <span>장소</span><span>{safety.place || "-"}</span>
          <span>개요</span><span>{safety.summary || "-"}</span>
        </div>
      </div>
    );
  }

  if (draft.type === "expense_create") {
    const expense = draft.payload?.expense || {};
    const items = getExpenseItems(expense);
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          <span>지출일</span><span>{expense.date || "-"}</span>
          <span>차량</span><span>{expense.car_number || "-"}</span>
          <span>기사</span><span>{expense.human_name || "-"}</span>
          {items.length > 0 ? (
            items.map((item) => (
              <React.Fragment key={item.key}>
                <span>{item.label}</span>
                <span>{item.amount.toLocaleString()}원</span>
              </React.Fragment>
            ))
          ) : (
            <>
              <span>지출 항목</span>
              <span>-</span>
            </>
          )}
        </div>
      </div>
    );
  }

  if (draft.type === "vehicle_update") {
    const before = draft.beforeData?.car || {};
    const updates = draft.payload?.updates || {};
    const changes = draft.afterData?.changes || getVehicleChangeItems(before, updates);
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {changes.map((item) => (
            <React.Fragment key={item.key}>
              <span>{item.label}</span>
              <span>{String(item.before || "-")} → {String(item.after || "-")}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (draft.type === "vehicle_create") {
    const vehicle = draft.payload?.vehicle || {};
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          <span>차량번호</span><span>{vehicle.car_number || "-"}</span>
          <span>차명</span><span>{vehicle.car_name || "-"}</span>
          <span>기사명</span><span>{vehicle.driver || "-"}</span>
          <span>핸드폰</span><span>{vehicle.phone || "-"}</span>
          <span>기사 이메일</span><span>{vehicle.driver_email || "-"}</span>
          <span>년식</span><span>{vehicle.year || "-"}</span>
          <span>승차인원</span><span>{vehicle.seat || "-"}</span>
        </div>
      </div>
    );
  }

  if (draft.type === "driver_update") {
    const before = draft.beforeData?.driver || {};
    const updates = draft.payload?.updates || {};
    const changes = draft.afterData?.changes || getDriverChangeItems(before, updates);
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {changes.map((item) => (
            <React.Fragment key={item.key}>
              <span>{item.label}</span>
              <span>{String(item.before || "-")} → {String(item.after || "-")}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (draft.type === "driver_create") {
    const driver = draft.payload?.driver || {};
    const items = getDriverCreateItems(driver);
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {items.map((item) => (
            <React.Fragment key={item.key}>
              <span>{item.label}</span>
              <span>{String(item.value || "-")}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (draft.type === "user_create") {
    const user = draft.payload?.user || {};
    const items = getUserCreateItems(user);
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {items.map((item) => (
            <React.Fragment key={item.key}>
              <span>{item.label}</span>
              <span>{item.key === "password" ? "****" : String(item.value || "-")}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (draft.type === "bug_report_create") {
    const report = draft.payload?.report || {};
    const items = getBugReportItems(report);
    return (
      <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-900">{draft.title}</div>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {items.map((item) => (
            <React.Fragment key={item.key}>
              <span>{item.label}</span>
              <span className="whitespace-pre-wrap">{String(item.value || "-")}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded border bg-white p-3 text-xs text-gray-700">
      <div className="font-semibold text-gray-900">{draft.title}</div>
      <div className="mt-1 grid grid-cols-2 gap-1">
        <span>영향 예약</span><span>{updates.length}건</span>
        <span>대체 기사</span><span>{selected.driver || "-"}</span>
        <span>대체 차량</span><span>{selected.car_number_for_reservation || selected.car_number || "-"}</span>
        <span>검증</span><span>{draft.validation?.conflict_rule || "-"}</span>
      </div>
    </div>
  );
}

export default function AiOpsAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [desktopBottom, setDesktopBottom] = useState(16);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "무엇을 처리할까요?" },
  ]);
  const [pendingIntent, setPendingIntent] = useState("");
  const [pendingCommand, setPendingCommand] = useState({});
  const [draft, setDraft] = useState(null);
  const [lastAiResult, setLastAiResult] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const speechBaseRef = useRef("");
  const finalTranscriptRef = useRef("");
  const restartTimeoutRef = useRef(null);

  const isLoggedIn = !!localStorage.getItem("email");
  const canSend = useMemo(() => input.trim() && !busy, [input, busy]);

  useEffect(() => {
    const footer = document.querySelector("footer.no-print");
    const updatePosition = () => {
      if (window.innerWidth < 768 || !footer) {
        setDesktopBottom(16);
        return;
      }
      const rect = footer.getBoundingClientRect();
      const footerVisible = rect.top < window.innerHeight && rect.bottom > 0;
      const bottom = footerVisible ? Math.max(16, window.innerHeight - rect.top + 12) : 16;
      setDesktopBottom((current) => current === bottom ? current : bottom);
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [location.pathname]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceSupported(false);
      return undefined;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "ko-KR";
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.onresult = (event) => {
      if (!shouldListenRef.current) return;
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript || "";
        if (result.isFinal) {
          finalTranscript = `${finalTranscript} ${transcript}`.trim();
        } else {
          interimTranscript = `${interimTranscript} ${transcript}`.trim();
        }
      }

      finalTranscriptRef.current = finalTranscript;
      setInput(`${speechBaseRef.current} ${finalTranscript} ${interimTranscript}`.trim());
    };

    recognition.onerror = (event) => {
      shouldListenRef.current = false;
      setListening(false);
      const message =
        event.error === "not-allowed"
          ? "마이크 권한이 거부되었습니다. 브라우저 주소창의 권한 설정을 확인해 주세요."
          : "음성을 인식하지 못했습니다. 다시 시도해 주세요.";
      setMessages((prev) => [...prev, { role: "assistant", text: message }]);
    };

    recognition.onend = () => {
      if (shouldListenRef.current) {
        speechBaseRef.current = `${speechBaseRef.current} ${finalTranscriptRef.current}`.trim();
        finalTranscriptRef.current = "";
        restartTimeoutRef.current = window.setTimeout(() => {
          restartTimeoutRef.current = null;
          if (!shouldListenRef.current) return;
          try {
            recognition.start();
          } catch (e) {
            setListening(false);
            shouldListenRef.current = false;
          }
        }, 250);
        return;
      }

      setListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      shouldListenRef.current = false;
      window.clearTimeout(restartTimeoutRef.current);
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  if (!isLoggedIn) return null;

  const push = (message) => {
    setMessages((prev) => [...prev, message]);
  };

  const buildDraft = async (aiResult, userMessage) => {
    if (aiResult.intent === "create_reservation_draft") {
      return makeReservationDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "driver_change_draft") {
      return makeDriverChangeDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "safety_create_draft") {
      return makeSafetyDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "expense_create_draft") {
      return makeExpenseDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "vehicle_update_draft") {
      return makeVehicleUpdateDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "vehicle_create_draft") {
      return makeVehicleCreateDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "driver_update_draft") {
      return makeDriverUpdateDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "driver_create_draft") {
      return makeDriverCreateDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "user_create_draft") {
      return makeUserCreateDraft(aiResult, userMessage);
    }
    if (aiResult.intent === "bug_report_create_draft") {
      return makeBugReportDraft(aiResult, userMessage);
    }
    return { ok: false, reply: aiResult.reply || "처리할 수 있는 요청을 찾지 못했습니다." };
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || busy) return;

    const wasListening = shouldListenRef.current;
    shouldListenRef.current = false;
    window.clearTimeout(restartTimeoutRef.current);
    if (wasListening) recognitionRef.current?.stop();
    setListening(false);
    speechBaseRef.current = "";
    finalTranscriptRef.current = "";
    setInput("");
    setBusy(true);
    push({ role: "user", text });

    try {
      const aiResult = await interpretAiOpsMessage(text, {
        previousIntent: pendingIntent,
        previousCommand: pendingCommand,
      });
      setLastAiResult(aiResult);

      if (aiResult.missing_fields?.length) {
        setPendingIntent(aiResult.intent);
        setPendingCommand(aiResult.command || {});
        push({ role: "assistant", text: aiResult.reply });
        return;
      }

      if (aiResult.intent === "reservation_search") {
        const command = aiResult.command || {};
        setPendingIntent("");
        setPendingCommand({});
        setDraft(null);
        navigate(buildReservationSearchPath(command));
        push({
          role: "assistant",
          text: `${command.date || ""} ${command.user ? `${command.user} 사용자 ` : ""}예약 검색 화면으로 이동했습니다.`.trim(),
        });
        setOpen(false);
        return;
      }

      const nextDraft = await buildDraft(aiResult, text);
      if (!nextDraft.ok) {
        setPendingIntent(aiResult.intent);
        setPendingCommand(aiResult.command || {});
        push({ role: "assistant", text: nextDraft.reply || "초안을 만들지 못했습니다." });
        return;
      }

      setPendingIntent("");
      setPendingCommand({});
      setDraft(nextDraft);
      push({ role: "assistant", text: "초안을 만들었습니다. 승인 전에는 실제 DB에 반영하지 않습니다.", draft: nextDraft });
    } catch (e) {
      push({ role: "assistant", text: `처리 실패: ${userErrorMessage(e)}` });
    } finally {
      setBusy(false);
    }
  };

  const handleVoiceInput = () => {
    if (!voiceSupported || !recognitionRef.current) {
      push({
        role: "assistant",
        text: "이 브라우저는 음성 인식을 지원하지 않습니다. Chrome 또는 Edge에서 다시 시도해 주세요.",
      });
      return;
    }

    if (listening) {
      shouldListenRef.current = false;
      window.clearTimeout(restartTimeoutRef.current);
      recognitionRef.current.stop();
      setListening(false);
      return;
    }

    try {
      speechBaseRef.current = input.trim();
      finalTranscriptRef.current = "";
      shouldListenRef.current = true;
      recognitionRef.current.start();
      setListening(true);
    } catch (e) {
      setListening(false);
      shouldListenRef.current = false;
      push({ role: "assistant", text: "음성 인식을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요." });
    }
  };

  const handleCreateApproval = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      const approval = await createApprovalFromDraft(draft, lastAiResult || {});
      setDraft(null);
      push({ role: "assistant", text: `승인 요청을 만들었습니다. 승인함 번호: ${approval.id}` });
    } catch (e) {
      push({
        role: "assistant",
        text: `승인 요청 저장 실패: ${userErrorMessage(e)}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCancelDraft = () => {
    setDraft(null);
    setPendingIntent("");
    setPendingCommand({});
    push({ role: "assistant", text: "초안을 취소했습니다. 승인 요청은 저장하지 않았습니다." });
  };

  const handleOpenReservationForm = () => {
    if (!draft || draft.type !== "reservation_create") return;
    const type = typeToRouteKey[draft.payload.summary.reservation_type] || "general";
    navigate("/reservation-apply", {
      state: {
        type,
        prefill: draftToPrefill(draft),
      },
    });
    setOpen(false);
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[120] no-print md:bottom-24 md:left-1/2 md:right-auto md:-translate-x-1/2"
      style={!open && window.innerWidth >= 768 ? { bottom: `${desktopBottom}px` } : undefined}
    >
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-blue-700"
        >
          AI 운영 비서
        </button>
      )}

      {open && (
        <div className="flex h-[calc(100vh-9rem)] max-h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded border bg-gray-50 shadow-2xl md:h-[560px]">
          <div className="flex items-center justify-between border-b bg-white px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">AI 운영 비서</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
            >
              닫기
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-3">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`max-w-[92%] rounded px-3 py-2 text-sm ${
                  message.role === "user"
                    ? "ml-auto bg-blue-600 text-white"
                    : "mr-auto border bg-white text-gray-800"
                }`}
              >
                <div className="whitespace-pre-wrap">{message.text}</div>
                <DraftSummary draft={message.draft} />
              </div>
            ))}
            {busy && (
              <div className="mr-auto rounded border bg-white px-3 py-2 text-sm text-gray-500">
                처리 중...
              </div>
            )}
          </div>

          {draft && (
            <div className="border-t bg-white p-3">
              <DraftSummary draft={draft} />
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                {draft.type === "reservation_create" && (
                  <button
                    type="button"
                    onClick={handleOpenReservationForm}
                    className="rounded border px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                    disabled={busy}
                  >
                    예약등록에서 확인
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCreateApproval}
                  className="rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={busy}
                >
                  승인 요청 저장
                </button>
                <button
                  type="button"
                  onClick={handleCancelDraft}
                  className="rounded border px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={busy}
                >
                  취소
                </button>
              </div>
            </div>
          )}

          <div className="border-t bg-white p-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) handleSend();
                }
              }}
              className="h-20 w-full resize-none rounded border px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="메시지를 입력하세요"
              disabled={busy}
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleVoiceInput}
                disabled={busy}
                className={`rounded border px-3 py-2 text-sm ${
                  listening
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                } disabled:opacity-50`}
                title={voiceSupported ? "마이크로 명령 입력" : "이 브라우저는 음성 인식을 지원하지 않습니다"}
              >
                {listening ? "듣기 종료" : "음성 입력"}
              </button>
              {listening && <span className="text-xs text-red-600">말씀하신 뒤 버튼을 다시 누르면 멈춥니다.</span>}
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                보내기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
